import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface Connection {
  id: string;
  full_name: string | null;
  username: string | null;
  profile_picture_url: string | null;
  belt_rank: string;
  academy_name: string | null;
}

/**
 * Returns the full list of the current user's connections for contact pickers.
 * Non-paginated — suitable for search/filter UIs.
 */
export function useConnections() {
  const { user } = useAuth();

  const { data: connections = [], isLoading, error } = useQuery({
    queryKey: ['connections-list', user?.id],
    queryFn: async (): Promise<Connection[]> => {
      if (!user) return [];

      // Step 1: get connected user IDs
      const { data: rows, error: idsError } = await supabase.rpc('get_my_connections');
      if (idsError) throw idsError;

      const ids: string[] = (rows ?? []).map((r: any) => r.user_id).filter(Boolean);
      if (ids.length === 0) return [];

      // Step 2: fetch full profiles including picture and academy
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, full_name, username, profile_picture_url, belt_rank, main_academy:main_academy_id(name)')
        .in('id', ids)
        .order('full_name');

      if (profileError) throw profileError;

      return (profiles ?? []).map((p: any) => ({
        id:                  p.id,
        full_name:           p.full_name ?? null,
        username:            p.username  ?? null,
        profile_picture_url: p.profile_picture_url ?? null,
        belt_rank:           p.belt_rank ?? 'white',
        academy_name:        p.main_academy?.name ?? null,
      }));
    },
    enabled: !!user,
  });

  return { connections, isLoading, error };
}
